import { useParsed, useOne } from "@refinedev/core";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import { Job, Batch, SheetData } from "../playground/types";
import { useEffect, useState, useMemo, useCallback } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { useGo } from "@refinedev/core";
import { getJobIds } from "../../api/batches";
import { getJobsByIds } from "../../api/phrasebook";
import ClientBatchGrid from "../../components/client/batch-grid";
import ClientBatchTable from "../../components/client/batch-table";
import ClientJobDialog from "../../components/client/job-dialog";
import ClientBatchList from "../../components/client/batch-list";
import ClientFiltersBox from "../../components/client/filters-box";
import ClientBulkActionBar from "../../components/client/bulk-action-bar";
import BulkResultsDialog from "../../components/job/list/bulk-results-dialog";
import { useClientBulkActions } from "../../hooks/useClientBulkActions";
import { getSheetData } from "../../api/sheet";

// Type for grouped jobs structure
type GroupedJobs = {
  [key: string]: Job[];
};

export default function ClientBatch() {
  const go = useGo();

  const { params } = useParsed<{ batchId: string }>();

  const batchId = params?.batchId;

  // State for view type (grid, list, table)
  const [viewType, setViewType] = useState<string>("grid");
  const [sheetData, setSheetData] = useState<SheetData | null>(null);
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [selectedJobIndex, setSelectedJobIndex] = useState<number>(-1);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  // Filter and grouping states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL"); // Use the same value as in filters-box
  const [clientNotesFilter, setClientNotesFilter] = useState<string>("ALL"); // Use the same value as in filters-box
  const [groupByColumn, setGroupByColumn] = useState<string>("NONE"); // Use the same value as in filters-box

  // Client bulk actions hook
  const bulkActions = useClientBulkActions({
    onRefresh: async () => {
      onRefresh();
    },
  });

  // Handle view type change
  const handleViewTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newViewType: string
  ) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };

  const { data: batchData, isLoading: isBatchLoading } = useOne<Batch>({
    resource: "batch",
    id: batchId || "",
    queryOptions: {
      enabled: !!batchId,
    },
  });

  // Helper function to get a value from master file data for a specific job and column
  const getMasterDataValue = (jobId: string, column: string): string => {
    if (!sheetData?.items) return "";

    const sheetDataIdColumn = sheetData.columns?.[0];

    // Find the sheet data item for this job
    const jobData = sheetData.items.find(
      (item) =>
        String(item[sheetDataIdColumn || ""]).trim() === String(jobId).trim()
    );
    if (!jobData) return "";

    // Return the column value or empty string if not found
    return jobData[column]?.toString() || "";
  };

  // Apply filtering to jobs based on search query, status, and client notes
  const filteredJobs = useMemo(() => {
    return jobs?.filter((job) => {
      // Search query filter (checks both SKU and title)
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const skuMatch =
          job.master_id?.toLowerCase().includes(searchLower) || false;
        const titleMatch =
          job.title?.toLowerCase().includes(searchLower) || false;

        if (!skuMatch && !titleMatch) {
          return false;
        }
      }

      // Status filter - only filter if not "ALL"
      if (selectedStatus !== "ALL") {
        if (job.status !== selectedStatus) {
          return false;
        }
      }

      // Client notes filter - only filter if not "ALL"
      if (clientNotesFilter !== "ALL") {
        const hasClientNotes =
          job.client_notes && job.client_notes.trim() !== "";

        if (clientNotesFilter === "WITH_NOTES" && !hasClientNotes) {
          return false;
        }

        if (clientNotesFilter === "WITHOUT_NOTES" && hasClientNotes) {
          return false;
        }
      }

      return true;
    });
  }, [jobs, searchQuery, selectedStatus, clientNotesFilter, sheetData]);

  // Group jobs by the selected column
  const groupedJobs = useMemo(() => {
    // If no grouping is selected or no column is found, return a single group with all jobs
    if (
      groupByColumn === "NONE" ||
      !sheetData?.columns?.includes(groupByColumn)
    ) {
      return null;
    }

    // Group jobs by the selected column value
    return filteredJobs?.reduce<GroupedJobs>((groups, job) => {
      // Get the value for this column from the master data
      const groupValue = getMasterDataValue(job.master_id, groupByColumn);

      // Use "(Empty)" if no value is found
      const groupKey = groupValue || "(Empty)";

      // Add the job to the appropriate group
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(job);

      return groups;
    }, {});
  }, [filteredJobs, groupByColumn, sheetData]);

  const onRefresh = (toDeleteJobIds?: string[]) => {
    if (toDeleteJobIds) {
      setJobIds(jobIds.filter((id) => !toDeleteJobIds.includes(id)));
    }

    getJobsByIds({ jobIds, batchId: String(batchId) }).then((jobs) => {
      setJobs(jobs.data.data as unknown as Job[]);
    });
  };

  useEffect(() => {
    if (!batchId) return;

    getJobIds({ batchId: String(batchId) }).then((jobIds) => {
      setJobIds(jobIds.data.data);
    });

    getSheetData(String(batchId)).then((sheetData) => {
      setSheetData(sheetData.data.data);
    });
  }, [batchId]);

  useEffect(() => {
    if (!batchId || !jobIds || jobIds.length === 0) return;

    onRefresh();
  }, [jobIds, batchId]);

  useEffect(() => {
    if (!filteredJobs || !selectedJob) return;

    const selectedJobIndex = filteredJobs.findIndex(
      (job) => job.id === selectedJob.id
    );
    setSelectedJobIndex(selectedJobIndex);

    const hasPrevious = selectedJobIndex > 0;
    const hasNext = selectedJobIndex < (filteredJobs?.length || 0) - 1;

    setHasPrevious(Boolean(hasPrevious));
    setHasNext(Boolean(hasNext));
  }, [filteredJobs, selectedJob]);

  const onPrevious = useCallback(() => {
    if (hasPrevious) {
      setSelectedJob(filteredJobs?.[selectedJobIndex - 1] || null);
    }
  }, [hasPrevious, selectedJobIndex, filteredJobs]);

  const onNext = useCallback(() => {
    if (hasNext) {
      setSelectedJob(filteredJobs?.[selectedJobIndex + 1] || null);
    }
  }, [hasNext, selectedJobIndex, filteredJobs]);

  // Clear bulk selection when view type changes
  useEffect(() => {
    if (viewType !== "list") {
      bulkActions.handleClearSelection();
      if (bulkActions.showBulkActions) {
        bulkActions.toggleBulkActions();
      }
    }
  }, [viewType]);

  return (
    <>
      {selectedJob && batchData?.data && sheetData && (
        <ClientJobDialog
          job={selectedJob}
          updateJob={(job: Job) => {
            // Update jobs array safely and update selected job simultaneously
            setJobs((prevJobs) =>
              prevJobs
                ? prevJobs.map((prevJob) =>
                    prevJob.id === job.id ? job : prevJob
                  )
                : []
            );
            // Update the selected job as well
            setSelectedJob(job);
          }}
          batch={batchData?.data}
          sheetData={sheetData}
          onClose={() => setSelectedJob(null)}
          onRefresh={onRefresh}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPrevious={onPrevious}
          onNext={onNext}
        />
      )}

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              go({
                to: "/review",
              });
            }}
            variant="text"
            size="small"
            sx={{ mr: 2 }}
          >
            Back
          </Button>

          {isBatchLoading ? (
            <Typography variant="h5" component="h1">
              <CircularProgress size={20} sx={{ mr: 1 }} />
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="h6">{batchData?.data?.name}</Typography>
                {batchData?.data?.brand_name && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {batchData?.data?.brand_name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                {viewType === "list" && (
                  <Button
                    variant={
                      bulkActions.showBulkActions ? "contained" : "outlined"
                    }
                    color="success"
                    size="small"
                    onClick={bulkActions.toggleBulkActions}
                  >
                    {bulkActions.showBulkActions
                      ? "Exit Bulk Mode"
                      : "Bulk Approve"}
                  </Button>
                )}

                <ToggleButtonGroup
                  value={viewType}
                  exclusive
                  onChange={handleViewTypeChange}
                  aria-label="view type"
                  size="small"
                >
                  <Tooltip title="Grid View">
                    <ToggleButton value="grid" aria-label="grid view">
                      <GridViewIcon />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="List View">
                    <ToggleButton value="list" aria-label="list view">
                      <ViewListIcon />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="CSV-Like Table View">
                    <ToggleButton value="table" aria-label="table view">
                      <TableRowsIcon />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {sheetData && (
          <ClientFiltersBox
            sheetData={sheetData}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            clientNotesFilter={clientNotesFilter}
            setClientNotesFilter={setClientNotesFilter}
            groupByColumn={groupByColumn}
            setGroupByColumn={setGroupByColumn}
          />
        )}

        {/* Content area */}
        {(() => {
          if (!batchId) {
            return <Alert severity="warning">Batch not found.</Alert>;
          }

          if (jobs === null || sheetData === null) {
            return (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <CircularProgress />
              </Box>
            );
          }

          if (jobs.length === 0) {
            return <Alert severity="info">No SKUs on this batch.</Alert>;
          }

          // Main content with jobs grid
          return (
            <Box>
              {/* Bulk Action Bar for List View */}
              {viewType === "list" && bulkActions.showBulkActions && (
                <ClientBulkActionBar
                  selectedJobsCount={bulkActions.selectedJobs.size}
                  onApprove={bulkActions.handleBulkApprove}
                  onClear={bulkActions.handleClearSelection}
                  isLoading={bulkActions.bulkLoading}
                />
              )}

              {/* Render different views based on viewType */}
              {viewType === "grid" && (
                <ClientBatchGrid
                  jobs={filteredJobs || []}
                  groupedJobs={groupedJobs}
                  onJobClick={(job: Job) => setSelectedJob(job)}
                />
              )}
              {viewType === "list" && (
                <ClientBatchList
                  jobs={filteredJobs || []}
                  groupedJobs={groupedJobs}
                  onJobClick={(job: Job) => setSelectedJob(job)}
                  showBulkActions={bulkActions.showBulkActions}
                  selectedJobs={bulkActions.selectedJobs}
                  onJobSelectionChange={bulkActions.handleJobSelectionChange}
                  onSelectAll={bulkActions.handleSelectAll}
                  canSelectJob={bulkActions.canSelectJob}
                />
              )}

              {/* Bulk Results Dialog */}
              <BulkResultsDialog
                open={bulkActions.bulkResultsDialogOpen}
                onClose={bulkActions.closeBulkResultsDialog}
                results={bulkActions.bulkResults}
              />
            </Box>
          );
        })()}
      </Container>

      {viewType === "table" && sheetData && (
        <ClientBatchTable
          jobs={filteredJobs || []}
          batch={batchData?.data}
          sheetData={sheetData}
          // onJobClick={(job: Job) => setSelectedJob(job)}
        />
      )}
    </>
  );
}
